import { ArrowBack as BackIcon, Book as BookIcon, Home as HomeIcon } from '@mui/icons-material'
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React from 'react'
import { Title, useTranslate } from 'react-admin'
import { useNavigate, useParams } from 'react-router-dom'

const DocumentationViewer: React.FC = () => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const { language = 'en', document = 'overview' } = useParams()

  const currentLanguage = language as 'en' | 'zh'
  const currentDocument = document as string

  const handleBackToHelp = () => {
    navigate('/help')
  }

  const renderDocumentContent = () => {
    switch (currentDocument) {
      case 'overview':
        return renderOverview()
      case 'features':
        return renderFeatures()
      case 'tables':
        return renderTables()
      case 'charts':
        return renderCharts()
      case 'faq':
        return renderFAQ()
      default:
        return renderNotFound()
    }
  }

  const renderOverview = () => {
    if (currentLanguage === 'zh') {
      return (
        <Box>
          <Typography variant="h4" gutterBottom>
            VetPo 系统概述
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            VetPo是一个专为兽医设计的综合门户应用程序，用于宠物的连续血糖监测(CGM)。
          </Alert>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            核心功能
          </Typography>

          <List>
            <ListItem>
              <ListItemText primary="实时监测" secondary="24/7血糖水平跟踪，多设备支持，实时警报" />
            </ListItem>
            <ListItem>
              <ListItemText primary="临床分析" secondary="趋势分析，时间范围计算，活动关联" />
            </ListItem>
            <ListItem>
              <ListItemText primary="数据管理" secondary="宠物档案，设备注册，活动记录，医疗记录" />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            血糖监测基础
          </Typography>

          <Typography variant="h6" gutterBottom>
            理解血糖单位
          </Typography>
          <Typography paragraph>
            • <strong>mmol/L</strong>（毫摩尔/升）: 国际标准单位
            <br />• <strong>mg/dL</strong>（毫克/分升）: 美国标准单位
            <br />• <strong>换算</strong>: 1 mmol/L = 18.0182 mg/dL
          </Typography>

          <Typography variant="h6" gutterBottom>
            目标血糖范围
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>动物</strong>
                  </TableCell>
                  <TableCell>
                    <strong>目标范围</strong>
                  </TableCell>
                  <TableCell>
                    <strong>高值区间</strong>
                  </TableCell>
                  <TableCell>
                    <strong>极高</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>犬类</TableCell>
                  <TableCell>4.4-11.1 mmol/L</TableCell>
                  <TableCell>11.1-22.2 mmol/L</TableCell>
                  <TableCell>&gt;22.2 mmol/L</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>猫类</TableCell>
                  <TableCell>4.4-16.7 mmol/L</TableCell>
                  <TableCell>16.7-27.8 mmol/L</TableCell>
                  <TableCell>&gt;27.8 mmol/L</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>马类</TableCell>
                  <TableCell>3.9-6.6 mmol/L</TableCell>
                  <TableCell>6.6-13.9 mmol/L</TableCell>
                  <TableCell>&gt;13.9 mmol/L</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>兔类</TableCell>
                  <TableCell>7.2-12.0 mmol/L</TableCell>
                  <TableCell>12.0-13.9 mmol/L</TableCell>
                  <TableCell>&gt;13.9 mmol/L</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography paragraph sx={{ mt: 2 }}>
            所有物种的<strong>极低</strong>均为 &lt;3.0 mmol/L；<strong>低值区间</strong>从 3.0
            到该物种的目标下限。血糖区间按物种区分（默认按犬类显示）。
          </Typography>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <strong>重要提示</strong>:
            临床决策请始终咨询合格的兽医师。VetPo提供数据和分析工具，但不能替代专业的兽医判断。
          </Alert>
        </Box>
      )
    }

    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          VetPo System Overview
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          VetPo is a comprehensive veterinary portal application designed for continuous glucose
          monitoring (CGM) of pets.
        </Alert>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Key Capabilities
        </Typography>

        <List>
          <ListItem>
            <ListItemText
              primary="Real-Time Monitoring"
              secondary="24/7 glucose level tracking, multiple device support, real-time alerts"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Clinical Analytics"
              secondary="Trend analysis, time in range calculations, activity correlation"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Data Management"
              secondary="Pet profiles, device registry, activity logging, medical records"
            />
          </ListItem>
        </List>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Glucose Monitoring Fundamentals
        </Typography>

        <Typography variant="h6" gutterBottom>
          Understanding Glucose Units
        </Typography>
        <Typography paragraph>
          • <strong>mmol/L</strong> (millimoles per liter): International standard unit
          <br />• <strong>mg/dL</strong> (milligrams per deciliter): US standard unit
          <br />• <strong>Conversion</strong>: 1 mmol/L = 18.0182 mg/dL
        </Typography>

        <Typography variant="h6" gutterBottom>
          Target Glucose Ranges
        </Typography>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Species</strong>
                </TableCell>
                <TableCell>
                  <strong>Target Range</strong>
                </TableCell>
                <TableCell>
                  <strong>High Zone</strong>
                </TableCell>
                <TableCell>
                  <strong>Very High</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Dogs</TableCell>
                <TableCell>4.4-11.1 mmol/L</TableCell>
                <TableCell>11.1-22.2 mmol/L</TableCell>
                <TableCell>&gt;22.2 mmol/L</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Cats</TableCell>
                <TableCell>4.4-16.7 mmol/L</TableCell>
                <TableCell>16.7-27.8 mmol/L</TableCell>
                <TableCell>&gt;27.8 mmol/L</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Horses</TableCell>
                <TableCell>3.9-6.6 mmol/L</TableCell>
                <TableCell>6.6-13.9 mmol/L</TableCell>
                <TableCell>&gt;13.9 mmol/L</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Rabbits</TableCell>
                <TableCell>7.2-12.0 mmol/L</TableCell>
                <TableCell>12.0-13.9 mmol/L</TableCell>
                <TableCell>&gt;13.9 mmol/L</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography paragraph sx={{ mt: 2 }}>
          <strong>Very Low</strong> is &lt;3.0 mmol/L for all species; the <strong>Low</strong> zone
          runs from 3.0 up to each species' target minimum. Glucose zones are species-specific
          (shown with dog defaults).
        </Typography>

        <Alert severity="warning" sx={{ mt: 3 }}>
          <strong>Important</strong>: Always consult with qualified veterinarians for clinical
          decisions. VetPo provides data and analysis tools but does not replace professional
          veterinary judgment.
        </Alert>
      </Box>
    )
  }

  const renderFeatures = () => {
    if (currentLanguage === 'zh') {
      return (
        <Box>
          <Typography variant="h4" gutterBottom>
            VetPo 功能指南
          </Typography>

          <Typography variant="body1" paragraph>
            本指南详细介绍VetPo中可用的所有功能以及如何访问它们。
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            控制面板
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            导航: 登录后的主页面
          </Typography>
          <Typography paragraph>
            控制面板是监测所有宠物血糖数据的中央枢纽。包含快速统计、最近更新列表、宠物选择和实时监测功能。
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            主要功能模块
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="🐕 宠物管理"
                secondary="管理宠物记录和基本信息，包括品种、年龄、体重等"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="🔬 设备管理" secondary="管理CGM设备及其分配，监控设备状态" />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="📊 血糖指标"
                secondary="查看和分析血糖测量数据，筛选和排序功能"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="📝 活动管理"
                secondary="跟踪影响血糖的事件：胰岛素、食物、运动"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="🏥 电子病历" secondary="临床文档和病史管理" />
            </ListItem>
          </List>
        </Box>
      )
    }

    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          VetPo Features Guide
        </Typography>

        <Typography variant="body1" paragraph>
          This guide provides detailed information about all features available in VetPo and how to
          access them.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Dashboard
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Navigation: Main page after login
        </Typography>
        <Typography paragraph>
          The dashboard is your central hub for monitoring glucose data across all pets. Features
          include quick statistics, recently updated lists, pet selection, and real-time monitoring.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Core Feature Modules
        </Typography>

        <List>
          <ListItem>
            <ListItemText
              primary="🐕 Pet Management"
              secondary="Manage pet records and basic information including breed, age, weight, and client details"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="🔬 Device Management"
              secondary="Manage CGM devices and their assignments, monitor device status and connectivity"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="📊 Glucose Metrics"
              secondary="View and analyze glucose measurement data with filtering and sorting capabilities"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="📝 Activity Management"
              secondary="Track events affecting glucose levels: insulin administration, food intake, exercise"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="🏥 Electronic Medical Records"
              secondary="Clinical documentation and medical history management"
            />
          </ListItem>
        </List>
      </Box>
    )
  }

  const renderTables = () => {
    if (currentLanguage === 'zh') {
      return (
        <Box>
          <Typography variant="h4" gutterBottom>
            VetPo 数据表格读取指南
          </Typography>

          <Typography paragraph>本指南说明如何读取和解释VetPo中的各种数据表格。</Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            通用表格功能
          </Typography>

          <List>
            <ListItem>
              <ListItemText primary="列标题" secondary="点击排序数据（升序/降序）" />
            </ListItem>
            <ListItem>
              <ListItemText primary="分页" secondary="使用页面控件浏览大型数据集" />
            </ListItem>
            <ListItem>
              <ListItemText primary="搜索" secondary="使用搜索框实时筛选记录" />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            血糖数据解读
          </Typography>

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>血糖范围</strong>
                  </TableCell>
                  <TableCell>
                    <strong>mmol/L</strong>
                  </TableCell>
                  <TableCell>
                    <strong>颜色编码</strong>
                  </TableCell>
                  <TableCell>
                    <strong>临床意义</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>极低</TableCell>
                  <TableCell>&lt;3.0</TableCell>
                  <TableCell>🟣 紫色</TableCell>
                  <TableCell>严重低血糖 - 立即处理</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>低</TableCell>
                  <TableCell>3.0-4.4</TableCell>
                  <TableCell>🔵 蓝色</TableCell>
                  <TableCell>低于目标 - 密切监测</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>目标范围</TableCell>
                  <TableCell>4.4-11.1</TableCell>
                  <TableCell>🟢 绿色</TableCell>
                  <TableCell>目标范围 - 理想控制</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>高</TableCell>
                  <TableCell>11.1-22.2</TableCell>
                  <TableCell>🟠 橙色</TableCell>
                  <TableCell>高于目标 - 需要关注</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>极高</TableCell>
                  <TableCell>&gt;22.2</TableCell>
                  <TableCell>🔴 红色</TableCell>
                  <TableCell>严重高血糖 - 立即处理</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )
    }

    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Reading Tables in VetPo
        </Typography>

        <Typography paragraph>
          This guide explains how to read and interpret the various data tables throughout VetPo.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          General Table Features
        </Typography>

        <List>
          <ListItem>
            <ListItemText
              primary="Column Headers"
              secondary="Click to sort data ascending/descending"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Pagination"
              secondary="Navigate through large datasets using page controls"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Search"
              secondary="Use the search box to filter records in real-time"
            />
          </ListItem>
        </List>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Glucose Level Interpretation
        </Typography>

        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Glucose Range</strong>
                </TableCell>
                <TableCell>
                  <strong>mmol/L</strong>
                </TableCell>
                <TableCell>
                  <strong>Color Code</strong>
                </TableCell>
                <TableCell>
                  <strong>Clinical Significance</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Very Low</TableCell>
                <TableCell>&lt;3.0</TableCell>
                <TableCell>🟣 Violet</TableCell>
                <TableCell>Severe hypoglycemia - immediate action</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Low</TableCell>
                <TableCell>3.0-4.4</TableCell>
                <TableCell>🔵 Blue</TableCell>
                <TableCell>Below target - monitor closely</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Target / In Range</TableCell>
                <TableCell>4.4-11.1</TableCell>
                <TableCell>🟢 Green</TableCell>
                <TableCell>Target range - optimal control</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>High</TableCell>
                <TableCell>11.1-22.2</TableCell>
                <TableCell>🟠 Orange</TableCell>
                <TableCell>Above target - needs attention</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Very High</TableCell>
                <TableCell>&gt;22.2</TableCell>
                <TableCell>🔴 Red</TableCell>
                <TableCell>Severe hyperglycemia - immediate action</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  const renderCharts = () => {
    if (currentLanguage === 'zh') {
      return (
        <Box>
          <Typography variant="h4" gutterBottom>
            VetPo 图表解读指南
          </Typography>

          <Typography paragraph>
            本指南说明如何阅读和解释VetPo中用于血糖监测和分析的各种图表类型。
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            图表类型概述
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="📈 血糖趋势图"
                secondary="血糖随时间变化的实时监测，支持缩放和筛选"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="🥧 时间范围图" secondary="在不同血糖范围内的时间分布饼图" />
            </ListItem>
            <ListItem>
              <ListItemText primary="📅 血糖日历视图" secondary="日历格式的每日血糖模式显示" />
            </ListItem>
            <ListItem>
              <ListItemText primary="📊 详细线图" secondary="特定时期的详细血糖变化线图" />
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            颜色编码解释
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Chip
              label="🟢 目标范围 (犬类 4.4-11.1 mmol/L，按物种区分)"
              color="success"
              variant="outlined"
            />
            <Chip label="🟡 注意范围 (需要监测)" color="warning" variant="outlined" />
            <Chip label="🔴 危险范围 (立即关注)" color="error" variant="outlined" />
            <Chip label="⚫ 无数据" variant="outlined" />
          </Box>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            交互功能
          </Typography>

          <List>
            <ListItem>
              <ListItemText primary="缩放和平移" secondary="鼠标滚轮缩放，点击拖动平移时间轴" />
            </ListItem>
            <ListItem>
              <ListItemText primary="悬停查看" secondary="将鼠标指向数据点查看确切数值" />
            </ListItem>
            <ListItem>
              <ListItemText primary="活动标记" secondary="图表上显示胰岛素、食物、运动时间点" />
            </ListItem>
          </List>
        </Box>
      )
    }

    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Understanding Charts in VetPo
        </Typography>

        <Typography paragraph>
          This guide explains how to read and interpret the various chart types available in VetPo
          for glucose monitoring and analysis.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Chart Types Overview
        </Typography>

        <List>
          <ListItem>
            <ListItemText
              primary="📈 Glucose Trend Chart"
              secondary="Real-time glucose monitoring over time with zoom and filter capabilities"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="🥧 Time in Range Chart"
              secondary="Distribution of time spent in different glucose ranges as pie chart"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="📅 Glucose Calendar View"
              secondary="Daily glucose patterns displayed in calendar format"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="📊 Detailed Line Charts"
              secondary="Detailed glucose progression charts for specific periods"
            />
          </ListItem>
        </List>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Color Coding Explanation
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Chip
            label="🟢 Target Range (dogs 4.4-11.1 mmol/L, species-specific)"
            color="success"
            variant="outlined"
          />
          <Chip label="🟡 Caution Ranges (monitor closely)" color="warning" variant="outlined" />
          <Chip label="🔴 Critical Ranges (immediate attention)" color="error" variant="outlined" />
          <Chip label="⚫ No Data Available" variant="outlined" />
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Interactive Features
        </Typography>

        <List>
          <ListItem>
            <ListItemText
              primary="Zoom and Pan"
              secondary="Mouse wheel to zoom, click and drag to pan through time"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Hover for Details"
              secondary="Point mouse over data points for exact values and timestamps"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Activity Markers"
              secondary="Charts show insulin, food, and exercise timing as overlay markers"
            />
          </ListItem>
        </List>
      </Box>
    )
  }

  const renderFAQ = () => {
    if (currentLanguage === 'zh') {
      return (
        <Box>
          <Typography variant="h4" gutterBottom>
            VetPo 常见问题解答
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            入门指南
          </Typography>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Q1: 如何访问VetPo系统？
              </Typography>
              <Typography>
                使用VetPo门户链接登录。您需要由诊所管理员提供的登录凭据。系统可在现代网页浏览器中运行。
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Q2: 如何选择要监测的宠物？
              </Typography>
              <Typography>
                1. 在控制面板上，找到"最近更新"部分
                <br />
                2. 在"宠物"列表中点击想要监测的宠物
                <br />
                3. 系统会自动显示该宠物关联的监测设备
                <br />
                4. 选择要查看数据的具体设备
                <br />
                5. 血糖数据和图表将自动更新
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Q3: 宠物的正常血糖范围是多少？
              </Typography>
              <Typography>
                <strong>犬类</strong>: 目标 4.4-11.1 mmol/L，高值 11.1-22.2，极高 &gt;22.2 mmol/L
                <br />
                <strong>猫类</strong>: 目标 4.4-16.7 mmol/L，高值 16.7-27.8，极高 &gt;27.8 mmol/L
                <br />
                危急情况需要立即兽医关注。
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            协作与共享
          </Typography>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Q4: 什么是"宠物共享"？宠物主人如何与我的诊所共享宠物？
              </Typography>
              <Typography>
                侧边栏的<strong>宠物共享</strong>
                用于兽医之间的共享：您可以将指定宠物（或全部宠物）共享给另一位兽医（如专科会诊），也可以查看其他兽医共享给您的宠物。
                <br />
                <br />
                宠物主人与诊所建立联系有两种方式：主人在 AdaPet
                手机应用中将您的诊所添加为他们的兽医；或您使用<strong>登记宠物主人</strong>
                替主人创建账户（主人会收到激活邮件），您的诊所将自动关联。
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Q5: "诊所团队"和"宠物主人"有什么区别？
              </Typography>
              <Typography>
                <strong>宠物主人</strong>是您诊所关联的宠物主人名录——在此浏览和搜索主人及其宠物。
                <br />
                <br />
                <strong>诊所团队</strong>
                是您诊所内部的员工团队：由诊所管理员创建并邀请兽医加入，成员自动可见诊所的所有宠物。它与宠物主人分组无关。
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            数据显示
          </Typography>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Q6: 如何更改血糖单位（mg/dL 或 mmol/L）？
              </Typography>
              <Typography>
                点击<strong>顶部应用栏的血糖单位按钮</strong>（在语言选择器旁边）即可在 mg/dL 与
                mmol/L 之间切换，所有图表和数值会自动换算。
                <br />
                <br />
                该设置会保存到您的账户——下次在任何浏览器登录时自动生效。
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Q7: 为什么宠物列表中的"兽医账户"列是空的？
              </Typography>
              <Typography>
                "兽医账户"列显示宠物关联的诊所。如果为空，说明该宠物尚未与任何诊所建立关联——请主人在
                AdaPet 应用中将您的诊所添加为兽医，或通过<strong>宠物共享</strong>建立共享。
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            技术支持
          </Typography>

          <Alert severity="info">
            <Typography>
              <strong>邮箱支持</strong>: support@adapetmedical.com - 24小时内回复
              <br />
              <strong>电话支持</strong>: 1-888-444-2618
              <br />
              <strong>紧急情况</strong>: 对于直接的兽医紧急情况，始终优先进行直接临床评估。
            </Typography>
          </Alert>
        </Box>
      )
    }

    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          VetPo Frequently Asked Questions
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Getting Started
        </Typography>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Q1: How do I access the VetPo system?
            </Typography>
            <Typography>
              Use the VetPo Portal link. You'll need login credentials provided by your practice
              administrator. The system works in modern web browsers.
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Q2: How do I select a pet for monitoring?
            </Typography>
            <Typography>
              1. On the dashboard, locate the "Recently Updated" section
              <br />
              2. Click on the desired pet in the "Pets" list
              <br />
              3. The system will display associated CGM devices
              <br />
              4. Select the specific device to view glucose data
              <br />
              5. Charts and data will automatically update
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Q3: What are normal glucose ranges for pets?
            </Typography>
            <Typography>
              <strong>Dogs</strong>: Target 4.4-11.1 mmol/L, High 11.1-22.2, Very High &gt;22.2
              mmol/L
              <br />
              <strong>Cats</strong>: Target 4.4-16.7 mmol/L, High 16.7-27.8, Very High &gt;27.8
              mmol/L
              <br />
              Critical levels require immediate veterinary attention.
            </Typography>
          </CardContent>
        </Card>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Collaboration & Sharing
        </Typography>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Q4: What is a "Pet Share"? How do clients share pets with my clinic?
            </Typography>
            <Typography>
              <strong>Pet Shares</strong> in the sidebar is vet-to-vet sharing: share specific pets
              (or all of your pets) with another veterinarian — e.g. for a specialist consult — and
              see pets other vets have shared with you.
              <br />
              <br />
              Clients connect to your clinic differently: either the client adds your clinic as
              their veterinarian in the AdaPet mobile app, or you create their account for them via{' '}
              <strong>Add New Client</strong> (the client receives an activation email and your
              clinic is linked automatically).
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Q5: What is the difference between "Clinic Team" and "Clients"?
            </Typography>
            <Typography>
              <strong>Clients</strong> is the directory of clients connected to your clinic — browse
              and search clients and their pets there.
              <br />
              <br />
              <strong>Clinic Team</strong> is a staff team inside your clinic: your clinic admin
              creates it and invites veterinarians, and team members automatically see all of the
              clinic&apos;s pets. It has nothing to do with grouping clients.
            </Typography>
          </CardContent>
        </Card>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Display & Data
        </Typography>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Q6: How do I change glucose units (mg/dL vs mmol/L)?
            </Typography>
            <Typography>
              Click the <strong>glucose-unit button in the top app bar</strong> (next to the
              language selector) to switch between mg/dL and mmol/L — all charts and values convert
              automatically.
              <br />
              <br />
              The setting saves to your account — the unit follows you to any browser you sign in
              from.
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Q7: Why is a pet&apos;s &quot;Vet Account&quot; column blank?
            </Typography>
            <Typography>
              The &quot;Vet Account&quot; column shows which clinic a pet is linked to. Blank means
              the pet has no clinic link yet — ask the client to add your clinic as their
              veterinarian in the AdaPet app, or establish a <strong>Pet Share</strong>.
            </Typography>
          </CardContent>
        </Card>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Technical Support
        </Typography>

        <Alert severity="info">
          <Typography>
            <strong>Email Support</strong>: support@adapetmedical.com - Response within 24 hours
            <br />
            <strong>Phone Support</strong>: 1-888-444-2618
            <br />
            <strong>Emergency Notice</strong>: For immediate veterinary emergencies, always
            prioritize direct clinical assessment over system data.
          </Typography>
        </Alert>
      </Box>
    )
  }

  const renderNotFound = () => {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          {currentLanguage === 'zh' ? '文档未找到' : 'Documentation Not Found'}
        </Typography>
        <Typography>
          {currentLanguage === 'zh'
            ? '请求的文档不存在。请检查URL或返回帮助页面。'
            : 'The requested documentation does not exist. Please check the URL or return to the help page.'}
        </Typography>
      </Box>
    )
  }

  const getDocumentTitle = () => {
    const titles = {
      en: {
        overview: 'System Overview',
        features: 'Features Guide',
        tables: 'Reading Tables',
        charts: 'Understanding Charts',
        faq: 'FAQ',
      },
      zh: {
        overview: '系统概述',
        features: '功能指南',
        tables: '读取表格',
        charts: '理解图表',
        faq: '常见问题',
      },
    }

    return titles[currentLanguage][currentDocument as keyof typeof titles.en] || 'Documentation'
  }

  return (
    <Box sx={{ p: 3 }}>
      <Title title={`${getDocumentTitle()} - VetPo Help`} />

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          onClick={handleBackToHelp}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {currentLanguage === 'zh' ? '帮助中心' : 'Help Center'}
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <BookIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {getDocumentTitle()}
        </Typography>
      </Breadcrumbs>

      {/* Back Button */}
      <Box sx={{ mb: 3 }}>
        <Link
          onClick={handleBackToHelp}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <BackIcon sx={{ mr: 1 }} />
          {currentLanguage === 'zh' ? '返回帮助中心' : 'Back to Help Center'}
        </Link>
      </Box>

      {/* Language Indicator */}
      <Box sx={{ mb: 3 }}>
        <Chip
          label={currentLanguage === 'zh' ? '简体中文' : 'English'}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Box>

      {/* Main Content */}
      <Card>
        <CardContent>{renderDocumentContent()}</CardContent>
      </Card>
    </Box>
  )
}

export default DocumentationViewer
